// app/layout.tsx
"use client";
import { ReactQueryProvider } from "./providers/ReactQueryProvider";
import "./globals.css";
import { ZustandProvider } from "./providers/ZustandProvider";
import { OpenAPIProvider } from "./providers/OpenAPIProvider";
import { GoogleTagManager } from "@next/third-parties/google";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { Noto_Sans_JP } from "next/font/google";

const notoSansJP = Noto_Sans_JP({
  subsets: ["latin"],
  weight: ["400", "500", "700"],
  display: "swap",
  variable: "--font-noto-sans-jp",
});

const nativeWebViewBottomOffsetScript = `
(function () {
  function applyBottomOffset() {
    document.documentElement.style.setProperty(
      "--app-native-tab-bar-height",
      "0px"
    );
  }
  applyBottomOffset();
  window.addEventListener("pageshow", applyBottomOffset);
  document.addEventListener("visibilitychange", applyBottomOffset);
})();
`;

function ConditionalGTM() {
  const pathname = usePathname();

  // Don't render GTM in preview mode
  if (pathname?.startsWith("/preview")) {
    return null;
  }

  return <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_ID || ""} />;
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const loadNewRelic = process.env.NEXT_PUBLIC_LOAD_NEW_RELIC === "true";

  return (
    <html lang="jp" className={notoSansJP.variable}>
      <head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        <script
          id="native-webview-bottom-offset"
          dangerouslySetInnerHTML={{ __html: nativeWebViewBottomOffsetScript }}
        />
        {loadNewRelic && (
          <Script
            id="new-relic"
            strategy="lazyOnload" // load after page is interactive
            src={process.env.NEXT_PUBLIC_NEW_RELIC_SCRIPT_URL}
          ></Script>
        )}
        <link
          rel="icon"
          href="/icons/favicon.png"
          type="image/png"
          sizes="any"
        />
      </head>
      <ConditionalGTM />
      <body suppressHydrationWarning>
        <OpenAPIProvider>
          <ReactQueryProvider>
            <ZustandProvider>{children}</ZustandProvider>
          </ReactQueryProvider>
        </OpenAPIProvider>
      </body>
    </html>
  );
}
